import React, { useState, useEffect, useRef } from "react";
import "./Opinions.scss";
import UserIcon from "./../../../assets/images/profile.gif";
import { useLanguage } from "../../../context/LanguageContext";
import moment from "moment";
import {
  ChevronLeft,
  ChevronRight,
  Star,
  StarBorder,
  FormatQuote,
  Person,
  CalendarToday,
} from "@mui/icons-material";

const Opinions = ({ ratings }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null);
  const [direction, setDirection] = useState("next");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const autoplayTimerRef = useRef(null);
  const sliderRef = useRef(null);
  const { t } = useLanguage();

  // Handle navigation with transition
  const goToPrevious = () => {
    if (isTransitioning) return;

    setPreviousIndex(currentIndex);
    setDirection("prev");
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? ratings.length - 1 : prevIndex - 1
    );
    resetAutoplayTimer();
  };

  const goToNext = () => {
    if (isTransitioning) return;

    setPreviousIndex(currentIndex);
    setDirection("next");
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === ratings.length - 1 ? 0 : prevIndex + 1
    );
    resetAutoplayTimer();
  };

  const goToIndex = (index) => {
    if (isTransitioning || index === currentIndex) return;

    setPreviousIndex(currentIndex);
    setDirection(index > currentIndex ? "next" : "prev");
    setIsTransitioning(true);
    setCurrentIndex(index);
    resetAutoplayTimer();
  };

  // Autoplay functionality
  useEffect(() => {
    if (!isPaused) {
      startAutoplayTimer();
    }

    return () => {
      clearTimeout(autoplayTimerRef.current);
    };
  }, [isPaused]);

  // Handle transition end
  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setPreviousIndex(null);
      }, 550);

      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  const startAutoplayTimer = () => {
    clearTimeout(autoplayTimerRef.current);
    autoplayTimerRef.current = setTimeout(() => {
      if (!isPaused) {
        goToNext();
      }
    }, 6000);
  };

  const resetAutoplayTimer = () => {
    clearTimeout(autoplayTimerRef.current);
    if (!isPaused) {
      startAutoplayTimer();
    }
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
    clearTimeout(autoplayTimerRef.current);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    startAutoplayTimer();
  };

  // Get classes for card transition
  const getCardClass = (index) => {
    let className = "opinion-card";

    if (index === currentIndex) {
      className += " active";
      if (isTransitioning) {
        className +=
          direction === "next" ? " slide-in-right" : " slide-in-left";
      }
    } else if (index === previousIndex && isTransitioning) {
      className +=
        direction === "next" ? " slide-out-left" : " slide-out-right";
    } else {
      className += " hidden";
    }

    return className;
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);

    for (let i = 0; i < 5; i++) {
      stars.push(
        i < fullStars ? (
          <Star key={i} className="star filled" />
        ) : (
          <StarBorder key={i} className="star empty" />
        )
      );
    }

    return stars;
  };

  if (!ratings || ratings.length === 0) {
    return (
      <div className="customer-opinions-container">
        <div className="heading">
          <h2>{t("opinions")}</h2>
          <p className="section-subtitle">{t("whatCustomersSay")}</p>
        </div>
        <div className="no-reviews">
          <FormatQuote className="quote-icon" />
          <p>{t("no_reviews_yet")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-opinions-container">
      <div className="heading">
        <FormatQuote className="section-icon" />
        <h2>{t("opinions")}</h2>
        <p className="section-subtitle">{t("whatCustomersSay")}</p>
      </div>

      <div
        className="opinions-slider"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className="nav-button prev"
          onClick={goToPrevious}
          disabled={isTransitioning}
          aria-label="Previous review"
        >
          <ChevronRight className="nav-icon" />
        </button>

        <div className="slider-wrapper" ref={sliderRef}>
          {ratings.map((ratingObj, index) => (
            <div key={ratingObj._id} className={getCardClass(index)}>
              <div className="quote-decoration">
                <FormatQuote className="quote-mark" />
              </div>

              <div className="review-content">
                <p className="review-text">{ratingObj.comment}</p>
              </div>

              <div className="customer-info">
                <div className="customer-avatar">
                  <img
                    src={UserIcon}
                    alt="User Avatar"
                    className="customer-image"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="avatar-fallback">
                    <Person className="person-icon" />
                  </div>
                </div>

                <div className="customer-details">
                  <div className="customer-main">
                    <h4 className="customer-name">
                      {ratingObj?.userName || "عميل مخفي"}
                    </h4>
                    <div className="rating-container">
                      <div className="rating">
                        {renderStars(ratingObj.rating)}
                      </div>
                      <span className="rating-number">
                        ({ratingObj.rating}/5)
                      </span>
                    </div>
                  </div>

                  <div className="customer-meta">
                    <div className="date-container">
                      <CalendarToday className="date-icon" />
                      <span className="customer-date">
                        {moment(ratingObj?.createdAt)
                          .locale("en")
                          .format("MMM DD, YYYY")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          className="nav-button next"
          onClick={goToNext}
          disabled={isTransitioning}
          aria-label="Next review"
        >
          <ChevronLeft className="nav-icon" />
        </button>
      </div>

      <div className="controls-section">
        <div className="indicators">
          {ratings.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentIndex ? "active" : ""}`}
              onClick={() => goToIndex(index)}
              aria-label={`Go to review ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Opinions;
